import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { resolveImg } from "@/lib/api";
import { searchGuides, track } from "@/lib/track";
import { GuideCard, GuideCta } from "@/components/site/GuideBits";

export default function Guides() {
  const { guides, guideCategories } = useData();
  const [q, setQ] = useState("");
  const results = useMemo(() => (q ? searchGuides(guides, q, guideCategories) : []), [q, guides, guideCategories]);
  const latest = guides.slice(0, 6);

  const onSearch = (v) => {
    setQ(v);
    if (v.length > 2) {
      const r = searchGuides(guides, v, guideCategories);
      track(r.length ? "search" : "search_no_result", v, "/guide");
    }
  };

  const schema = {
    "@context": "https://schema.org", "@type": "WebSite", name: "Guide Animaverde",
    url: "https://www.giardinianimaverde.it/guide",
    potentialAction: { "@type": "SearchAction", target: "https://www.giardinianimaverde.it/guide?q={query}", "query-input": "required name=query" },
  };

  return (
    <>
      <Seo title="Guide e risposte per la cura del giardino" description="Consigli pratici su prato, piante, potature, irrigazione, parassiti e manutenzione del verde, preparati da Animaverde." path="/guide" schema={schema} />
      <PageHero
        overline="Centro Guide sul Giardinaggio"
        title="Guide e risposte per la cura del giardino"
        subtitle="Consigli pratici su prato, piante, potature, irrigazione, parassiti e manutenzione del verde, preparati da Animaverde per aiutarti a prenderti cura del tuo giardino."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Guide</span></>}
      />

      <section className="bg-av-black py-14 sm:py-20">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          {/* Search */}
          <Reveal className="max-w-2xl mx-auto mb-14">
            <div className="relative">
              <Search size={20} className="absolute left-5 top-1/2 -translate-y-1/2 text-av-gray" />
              <input
                data-testid="guides-search"
                value={q}
                onChange={(e) => onSearch(e.target.value)}
                placeholder="Cerca una guida: 'prato giallo', 'quando potare', 'quanto costa'..."
                className="w-full rounded-full bg-av-green-dark/40 border border-white/15 pl-14 pr-5 py-4 text-av-white placeholder:text-av-gray/60 focus:border-av-lime outline-none"
                style={{ backgroundColor: "rgba(22,56,29,0.4)" }}
              />
            </div>
            {q.length > 2 && (
              <div className="mt-4 rounded-2xl border border-white/10 divide-y divide-white/10 overflow-hidden" data-testid="guides-search-results">
                {results.length > 0 ? results.slice(0, 6).map((g) => (
                  <Link key={g.id} to={`/guide/${g.slug}`} className="flex items-center justify-between gap-3 p-4 hover:bg-white/5">
                    <span className="text-av-white text-sm">{g.domanda || g.titolo}</span>
                    <ArrowUpRight size={15} className="text-av-lime shrink-0" />
                  </Link>
                )) : (
                  <div className="p-5 text-sm text-av-gray">
                    Nessuna guida trovata. <Link to="/chiedi-all-esperto" className="text-av-lime underline">Chiedi all'esperto</Link> o contattaci.
                  </div>
                )}
              </div>
            )}
          </Reveal>

          {/* Categories */}
          <h2 className="font-display text-3xl text-av-white mb-8">Sfoglia per categoria</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-16">
            {guideCategories.map((c, i) => (
              <Reveal key={c.id} delay={(i % 4) * 0.05}>
                <Link to={`/guide/categoria/${c.slug}`} data-testid={`guide-cat-${c.slug}`} className="group block relative overflow-hidden rounded-2xl aspect-[4/3]">
                  <img src={resolveImg(c.immagine)} alt={c.nome} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute inset-0 bg-gradient-to-t from-av-black/90 to-av-black/20" />
                  <span className="absolute bottom-3 left-4 right-4 font-display text-lg text-av-white">{c.nome}</span>
                </Link>
              </Reveal>
            ))}
          </div>

          {/* Latest guides */}
          <h2 className="font-display text-3xl text-av-white mb-8">Ultime guide pubblicate</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {latest.map((g, i) => <Reveal key={g.id} delay={(i % 3) * 0.06}><GuideCard g={g} /></Reveal>)}
          </div>

          <GuideCta />
        </div>
      </section>
    </>
  );
}
